"use client";

import { useMemo, useState } from "react";
import { FindingsAiSummary } from "@/components/aicheck/FindingsAiSummary";
import {
  ActionDecisionButtons,
  FeedbackButtons,
} from "@/components/aicheck/shared/StructuredResultView";
import type { AICheckRunResult } from "@/types/aicheck";

const SEVERITY_STYLES: Record<string, string> = {
  critical: "bg-red-100 text-red-700",
  error: "bg-red-100 text-red-700",
  warning: "bg-amber-100 text-amber-700",
  info: "bg-sky-100 text-sky-700",
};

const SEVERITY_SORT: Record<string, number> = {
  critical: 0,
  error: 1,
  warning: 2,
  info: 3,
};

const PREVIEW_COUNT = 10;

/** Expert Knowledge チェックの checkId（configs/ai-checks.yaml と一致） */
const EXPERT_NUANCE_CHECK_ID = "expert-nuance-check";

interface OverviewFinding {
  key: string;
  checkId: string;
  checkName: string;
  source: "ai" | "consistency";
  /** 専門家ノウハウ（Expert Knowledge）由来の指摘か */
  isExpert: boolean;
  /** 根拠に使われた専門知識の事実数（Expert Knowledge のみ） */
  factCount: number;
  severity: string;
  finding: string;
  recommendation: string | null;
  checkPoint: string | null;
  dayNumber: number | null;
  /** 元の指摘レコード（採否ボタン・知識フィードバック用） */
  raw: Record<string, unknown>;
}

function asString(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value : null;
}

/** 実行結果から全チェック横断の指摘リストを組み立てる（サーバ抽出と同じ規則） */
function collectFindings(result: AICheckRunResult): OverviewFinding[] {
  const findings: OverviewFinding[] = [];
  for (const check of result.aiResults ?? []) {
    const raw = (check.result as Record<string, unknown> | undefined)?.findings;
    if (!Array.isArray(raw)) continue;
    raw.forEach((item, index) => {
      if (!item || typeof item !== "object") return;
      const record = item as Record<string, unknown>;
      const findingText =
        asString(record.finding) ?? asString(record.description);
      if (!findingText) return;
      const isExpert = check.checkId === EXPERT_NUANCE_CHECK_ID;
      const factCount =
        isExpert && Array.isArray(record.usedFactIds)
          ? record.usedFactIds.filter(
              (id): id is string => typeof id === "string",
            ).length
          : 0;
      findings.push({
        key: `${check.checkId}:${index}`,
        checkId: check.checkId,
        checkName: check.checkName || check.checkId,
        source: "ai",
        isExpert,
        factCount,
        severity: asString(record.severity) ?? "info",
        finding: findingText,
        recommendation: asString(record.recommendation),
        checkPoint: asString(record.check_point),
        dayNumber:
          typeof record.day_number === "number" ? record.day_number : null,
        raw: record,
      });
    });
  }
  for (const rule of result.consistencyResults ?? []) {
    if (rule.passed) continue;
    findings.push({
      key: `consistency:${rule.ruleId}`,
      checkId: rule.ruleId,
      checkName: rule.ruleName,
      source: "consistency",
      isExpert: false,
      factCount: 0,
      severity: rule.severity,
      finding: rule.message,
      recommendation: null,
      checkPoint: rule.ruleName,
      dayNumber: null,
      raw: {},
    });
  }
  return findings.sort(
    (a, b) =>
      (SEVERITY_SORT[a.severity] ?? 9) - (SEVERITY_SORT[b.severity] ?? 9),
  );
}

/**
 * ia-ui Step2: 全チェック横断の指摘一覧。チェック種別のタブを渡り歩かずに
 * 重症度順で指摘を確認し、その場で「対応する / 不要」の判断ができる。
 */
export function FindingsOverview({ result }: { result: AICheckRunResult }) {
  const [severityFilter, setSeverityFilter] = useState<string | null>(null);
  const [checkFilter, setCheckFilter] = useState<string>("all");
  const [expanded, setExpanded] = useState(false);

  const all = useMemo(() => collectFindings(result), [result]);
  const checks = useMemo(() => {
    const map = new Map<string, string>();
    for (const f of all) {
      if (!map.has(f.checkId)) map.set(f.checkId, f.checkName);
    }
    return [...map.entries()];
  }, [all]);

  const severityCounts = useMemo(() => {
    const scoped =
      checkFilter === "all"
        ? all
        : all.filter((f) => f.checkId === checkFilter);
    return ["critical", "error", "warning", "info"]
      .map((severity) => ({
        severity,
        count: scoped.filter((f) => f.severity === severity).length,
      }))
      .filter(({ count }) => count > 0);
  }, [all, checkFilter]);

  const filtered = all.filter(
    (f) =>
      (checkFilter === "all" || f.checkId === checkFilter) &&
      (!severityFilter || f.severity === severityFilter),
  );
  const visible = expanded ? filtered : filtered.slice(0, PREVIEW_COUNT);
  const hiddenCount = filtered.length - visible.length;

  const projectId =
    result.projectId != null ? String(result.projectId) : undefined;
  const runId = (result as { runId?: string }).runId;

  if (all.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-gray-400">
        指摘はありません。
      </p>
    );
  }

  return (
    <div className="space-y-3">
      {/* AI要約（参考）— 非同期・使い捨て。ローカル LLM 不在なら非表示 */}
      <FindingsAiSummary runId={runId} />

      {/* フィルタ */}
      <div className="flex flex-wrap items-center gap-2">
        {severityCounts.map(({ severity, count }) => (
          <button
            key={severity}
            type="button"
            onClick={() => {
              setSeverityFilter((current) =>
                current === severity ? null : severity,
              );
              setExpanded(false);
            }}
            className={`rounded px-2 py-0.5 text-xs transition-opacity ${
              SEVERITY_STYLES[severity] ?? "bg-gray-100 text-gray-600"
            } ${severityFilter && severityFilter !== severity ? "opacity-30" : ""}`}
            title={`${severity} のみ表示（再クリックで解除）`}
          >
            {severity} {count}
          </button>
        ))}
        <select
          value={checkFilter}
          onChange={(e) => {
            setCheckFilter(e.target.value);
            setSeverityFilter(null);
            setExpanded(false);
          }}
          className="ml-auto h-8 rounded border border-gray-300 bg-white px-2 text-xs text-gray-700"
          aria-label="チェックで絞り込み"
        >
          <option value="all">すべてのチェック（{all.length}件）</option>
          {checks.map(([id, name]) => (
            <option key={id} value={id}>
              {name}
            </option>
          ))}
        </select>
      </div>

      {/* 指摘カード（重症度順） */}
      <div className="space-y-2">
        {visible.map((f) => (
          <div
            key={f.key}
            className="rounded-lg border border-gray-200 bg-white px-4 py-3"
          >
            <div className="flex flex-wrap items-center gap-2">
              <span
                className={`inline-flex shrink-0 items-center rounded px-1.5 py-0.5 text-[11px] font-medium ${
                  SEVERITY_STYLES[f.severity] ?? "bg-gray-100 text-gray-600"
                }`}
              >
                {f.severity}
              </span>
              {f.isExpert && (
                <span
                  className="inline-flex shrink-0 items-center rounded bg-blue-50 px-1.5 py-0.5 text-[11px] font-medium text-blue-700 ring-1 ring-inset ring-blue-200"
                  title="専門家ノウハウ（Expert Knowledge）由来の指摘です。詳細は「エキスパート知識」タブで根拠事実まで確認できます。"
                >
                  専門知識
                  {f.factCount > 0 && (
                    <span className="ml-1 text-blue-400">
                      根拠{f.factCount}
                    </span>
                  )}
                </span>
              )}
              <span className="text-xs font-medium text-gray-500">
                {f.checkName}
              </span>
              {f.checkPoint && f.checkPoint !== f.checkName && (
                <span className="text-xs text-gray-400">{f.checkPoint}</span>
              )}
              {f.dayNumber != null && (
                <span className="text-xs text-gray-400">Day {f.dayNumber}</span>
              )}
              {f.source === "consistency" && (
                <span className="text-xs text-gray-400">整合性ルール</span>
              )}
            </div>
            <p className="mt-1.5 whitespace-pre-wrap text-sm text-gray-800">
              {f.finding}
            </p>
            {f.recommendation && (
              <p className="mt-1 whitespace-pre-wrap text-xs text-gray-500">
                推奨: {f.recommendation}
              </p>
            )}
            {f.source === "ai" && projectId && (
              <div className="mt-2 flex flex-wrap items-center gap-3">
                {typeof f.raw.aspectId === "string" && f.raw.aspectId && (
                  <FeedbackButtons
                    aspectId={f.raw.aspectId}
                    usedFactIds={
                      Array.isArray(f.raw.usedFactIds)
                        ? f.raw.usedFactIds.filter(
                            (id): id is string => typeof id === "string",
                          )
                        : []
                    }
                    feedbackCtx={{ runId, projectId, checkId: f.checkId }}
                  />
                )}
                <ActionDecisionButtons
                  finding={f.raw}
                  feedbackCtx={{ runId, projectId, checkId: f.checkId }}
                />
              </div>
            )}
          </div>
        ))}
        {hiddenCount > 0 && (
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="w-full rounded-lg border border-dashed border-gray-300 py-2 text-xs text-gray-500 hover:bg-gray-50"
          >
            残り {hiddenCount} 件を表示（重要度の高い順に表示しています）
          </button>
        )}
        {expanded && filtered.length > PREVIEW_COUNT && (
          <button
            type="button"
            onClick={() => setExpanded(false)}
            className="w-full rounded-lg border border-dashed border-gray-200 py-1.5 text-[11px] text-gray-400 hover:bg-gray-50"
          >
            折りたたむ
          </button>
        )}
      </div>
    </div>
  );
}
